import React, { useMemo } from 'react';
import { useSelector } from 'react-redux';
import { useNavigate } from 'react-router-dom';

/**
 * SyncTrustBanner
 *
 * A persistent, non-dismissible trust indicator on the dashboard.
 * Gives field officers confidence that their data is safe without
 * using technical sync terminology.
 *
 * States:
 *   green  – everything has been uploaded to the server
 *   amber  – submissions are saved on device, waiting to upload
 *   red    – one or more submissions could not be uploaded and need review
 */
const SyncTrustBanner = () => {
  const navigate = useNavigate();
  const pendingCount = useSelector(state => state.sync?.pendingCount ?? 0);
  const syncError    = useSelector(state => state.sync?.error);
  const isOnline     = useSelector(state => state.ui?.networkStatus?.isOnline ?? navigator.onLine);

  // Derive how many submissions "need attention" from the error string
  // syncService sets: "${n} submissions need your attention"
  const attentionCount = useMemo(() => {
    if (!syncError) return 0;
    const match = syncError.match(/^(\d+)/);
    return match ? parseInt(match[1], 10) : 0;
  }, [syncError]);

  // Determine banner state
  const state = useMemo(() => {
    if (attentionCount > 0) return 'red';
    if (pendingCount > 0)   return 'amber';
    return 'green';
  }, [pendingCount, attentionCount]);

  const config = useMemo(() => {
    if (state === 'red') {
      return {
        bg:     'bg-red-50 border-red-200',
        icon:   '⚠️',
        text:   attentionCount === 1
          ? `1 submission needs your attention - tap to review`
          : `${attentionCount} submissions need your attention - tap to review`,
        textColor: 'text-red-800',
        clickable: true,
      };
    }
    if (state === 'amber') {
      const offlineNote = !isOnline ? ' – will upload when connected' : '';
      return {
        bg:     'bg-amber-50 border-amber-200',
        icon:   '⏳',
        text:   pendingCount === 1
          ? `1 submission saved on device${offlineNote}`
          : `${pendingCount} submissions saved on device${offlineNote}`,
        textColor: 'text-amber-800',
        clickable: true,
      };
    }
    return {
      bg:     'bg-green-50 border-green-200',
      icon:   '✅',
      text:   'All data uploaded',
      textColor: 'text-green-800',
      clickable: false,
    };
  }, [state, pendingCount, attentionCount, isOnline]);

  if (state === 'green') return null;

  return (
    <div
      role={config.clickable ? 'button' : 'status'}
      aria-live="polite"
      tabIndex={config.clickable ? 0 : undefined}
      onClick={config.clickable ? () => navigate('/sync') : undefined}
      onKeyDown={config.clickable ? e => e.key === 'Enter' && navigate('/sync') : undefined}
      className={[
        'w-full flex items-center gap-2.5 px-4 py-3 rounded-xl border',
        'transition-all duration-300',
        config.bg,
        config.clickable
          ? 'cursor-pointer hover:shadow-md active:scale-[0.99] select-none'
          : 'cursor-default',
      ].join(' ')}
    >
      <span className="text-lg leading-none flex-shrink-0" aria-hidden="true">
        {config.icon}
      </span>
      <span className={`text-sm font-semibold flex-1 leading-snug ${config.textColor}`}>
        {config.text}
      </span>
      {config.clickable && (
        <svg
          className={`w-4 h-4 flex-shrink-0 ${config.textColor} opacity-60`}
          fill="none"
          viewBox="0 0 24 24"
          stroke="currentColor"
          aria-hidden="true"
        >
          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 5l7 7-7 7" />
        </svg>
      )}
    </div>
  );
};

export default SyncTrustBanner;
